import React, { useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { normalizePhoneInput, isValidPhone, isValidEmail, requiredMessage, phoneMessage, emailMessage } from '../../utils/validation';
import { openExternalWithNotice } from '../../utils/externalLinks';
import { directionsUrl, GoogleLocationMap, normalizeLocation } from '../../components/maps/LocationMap';
import { contactMessageAPI } from '../../services/api';
import { phoneHref } from '../../utils/phone';
import './ContactPage.css';

export default function ContactPage() {
  const { tenantData, loading } = useTenant();
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading || !tenantData) return <div className="gp-loading">माहिती लोड होत आहे...</div>;
  const { tenant } = tenantData;
  const contactEmail = tenant?.contact_email || 'deulgaongadagram@gmail.com';
  const contactAddress = tenant?.address || [tenant?.name, tenant?.taluka, tenant?.district, 'Maharashtra'].filter(Boolean).join(', ');
  const mapQuery = encodeURIComponent(contactAddress || tenant?.name || 'Gram Panchayat');
  const mapEmbedUrl = `https://www.google.com/maps?q=${mapQuery}&output=embed`;
  const officePosition = normalizeLocation(tenant?.latitude, tenant?.longitude);
  const mapDirectionsUrl = directionsUrl(tenant?.latitude, tenant?.longitude, contactAddress);

  const setField = (field, value) => {
    const nextValue = field === 'phone' ? normalizePhoneInput(value) : value;
    setForm(prev => ({ ...prev, [field]: nextValue }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (status) setStatus('');
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const nextErrors = {};
    if (!form.name.trim() || form.name.trim().length < 3) nextErrors.name = 'कृपया किमान ३ अक्षरांचे नाव टाका.';
    if (!isValidPhone(form.phone)) nextErrors.phone = phoneMessage;
    if (!form.email.trim()) nextErrors.email = requiredMessage('ईमेल पत्ता');
    else if (!isValidEmail(form.email)) nextErrors.email = emailMessage;
    if (!form.message.trim() || form.message.trim().length < 10) nextErrors.message = 'कृपया किमान १० अक्षरांचा संदेश लिहा.';

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setStatus('');
      return;
    }

    setSubmitting(true);
    try {
      await contactMessageAPI.submit(tenant.slug, {
        name: form.name.trim(),
        phone: form.phone,
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setForm({ name: '', phone: '', email: '', message: '' });
      setStatus('तुमचा संदेश ग्रामपंचायत कार्यालयाला यशस्वीरित्या पाठवला आहे.');
    } catch (requestError) {
      const details = requestError.response?.data?.details;
      if (Array.isArray(details)) {
        setErrors(Object.fromEntries(details.map(item => [item.field, item.message])));
      }
      setStatus(requestError.response?.data?.error || 'संदेश पाठवता आला नाही. कृपया पुन्हा प्रयत्न करा.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-redesign">
      <section className="page-heading contact-hero"><span>मुख्यपृष्ठ › संपर्क</span><h1>संपर्क</h1><p>ग्रामपंचायत कार्यालयाशी संपर्क साधा.</p></section>
      <div className="contact-layout">
        <section className="contact-info-card">
          <h2>संपर्क माहिती</h2>
          <p><span>⌖</span><b>कार्यालयाचा पत्ता</b><strong>{tenant?.address || `${tenant?.name}, ${tenant?.taluka}, ${tenant?.district}`}</strong></p>
          <a className="contact-info-item" href={phoneHref(tenant?.contact_phone || '+91 1234567890')}><span>☎</span><b>दूरध्वनी</b><strong>{tenant?.contact_phone || '+91 1234567890'}</strong></a>
          <a className="contact-info-item" href={`mailto:${contactEmail}`}><span>✉</span><b>ईमेल</b><strong>{contactEmail}</strong></a>
          <p><span>◷</span><b>कार्यालय वेळ</b><strong>{tenant?.office_hours || 'सोमवार ते शनिवार, 10:00 AM - 05:00 PM'}</strong></p>
        </section>
        <section className="contact-map">
          {officePosition ? (
            <GoogleLocationMap latitude={tenant.latitude} longitude={tenant.longitude} title={tenant?.name || 'ग्रामपंचायत कार्यालय'} address={contactAddress} />
          ) : (
            <iframe
              title={`${tenant?.name || 'ग्रामपंचायत'} नकाशा`}
              src={mapEmbedUrl}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          )}
          <div>
            <strong>{tenant?.name || 'ग्रामपंचायत कार्यालय'}</strong>
            <small>{contactAddress}</small>
            <a href={mapDirectionsUrl} target="_blank" rel="noreferrer" onClick={event => openExternalWithNotice(event, mapDirectionsUrl)}>दिशा मिळवा</a>
          </div>
        </section>
        <section className="contact-form-card">
          <h2>आम्हाला संदेश पाठवा</h2>
          {status && <div className="contact-form-status">{status}</div>}
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <label className={errors.name ? 'field-error' : ''}>
                <input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="तुमचे नाव" />
                {errors.name && <small>{errors.name}</small>}
              </label>
              <label className={errors.phone ? 'field-error' : ''}>
                <input value={form.phone} onChange={e => setField('phone', e.target.value)} placeholder="मोबाईल नंबर" inputMode="numeric" maxLength={10} />
                {errors.phone && <small>{errors.phone}</small>}
              </label>
            </div>
            <label className={errors.email ? 'field-error' : ''}>
              <input value={form.email} onChange={e => setField('email', e.target.value)} placeholder="ईमेल पत्ता" />
              {errors.email && <small>{errors.email}</small>}
            </label>
            <label className={errors.message ? 'field-error' : ''}>
              <textarea value={form.message} onChange={e => setField('message', e.target.value)} placeholder="तुमचा संदेश" rows="5" />
              {errors.message && <small>{errors.message}</small>}
            </label>
            <button type="submit" disabled={submitting}>{submitting ? 'पाठवत आहे...' : 'संदेश पाठवा'}</button>
          </form>
        </section>
        <section className="contact-links-card"><h2>त्वरित दुवे</h2><a href="/#">नागरिक सेवा</a><a href="/#">नोटीस</a><a href="/#">योजना</a><a href="/#">डाउनलोड</a></section>
      </div>
    </div>
  );
}
